// Implementation Factory screens: the home (what is waiting for you, the project board, value and AI cost), requests,
// one request from the roadmap through the improvement cycle, tasks, one task, and a new request. People make every decision here: the API
// refuses approvals, deployments and decisions that come through MCP or Courage Chat.
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CommandBar } from "../Shell";
import { ErrorBox, ago } from "../ui";
import { useLoad } from "../hooks";
import { api } from "../api";
import { SUITE } from "../suite";
import { factory, CYCLE, DECISION_LABEL, STATE_LABEL, stepIndex, usd, val, when, type NewTask, type RequestDetail, type RequestRow, type Task, type TaskDetail } from "./factoryApi";
import { imp } from "../improve/impApi";
import { ProcessPicker, settleProcess, type ProcessValue } from "../improve/ProcessPicker";
import "../maintenance/mx.css";
import "./factory.css";
import { NumberCard } from "../analytics/NumberCard";
import { EnvTag, RequestPipeline } from "./Pipeline";
import { FactoryTimeline, RequestTimeline } from "./FactoryTimeline";
import { LeanRequest } from "./A3";

const CYCLE_STATES = CYCLE.flatMap((s) => [...s.states]);
const AREAS = SUITE.map((d) => ({ id: d.id, name: d.name }));
const areaName = (id: string | null) => (id ? AREAS.find((a) => a.id === id)?.name ?? id : "–");

function Chip({ state }: { state: string }) {
  const tone = ["closed", "approved", "deployed"].includes(state) ? "ok" : ["review", "deploy", "recatalog"].includes(state) ? "warn" : ["cancelled", "roadmap"].includes(state) ? "" : "info";
  return <span className={`if-chip ${tone}`}>{STATE_LABEL[state] ?? state}</span>;
}
const Bar = ({ children, status }: { children?: ReactNode; status?: ReactNode }) => <CommandBar status={status}>{children}</CommandBar>;

/** The improvement cycle as a process bar. On the roadmap, none of it has started; closed, all of it is done. */
function Steps({ state }: { state: string }) {
  const at = state === "closed" ? CYCLE.length : stepIndex(state);
  return (
    <div className="bpf" role="list" aria-label="Improvement cycle">
      {CYCLE.map((s, i) => (
        <div key={s.id} role="listitem" className={`bpf-stage ${i < at ? "done" : i === at ? "current" : ""}`} aria-current={i === at ? "step" : undefined}>
          <span className="name">{i + 1} {s.name}</span>
        </div>
      ))}
    </div>
  );
}

function Measure({ r }: { r: Pick<RequestRow, "measure" | "unit" | "currentValue" | "targetValue" | "better"> }) {
  if (!r.measure) return <span className="muted">No measure yet</span>;
  return <span className="if-measure"><span>{r.measure}</span><span className="v"><b>{val(r.currentValue, r.unit)}</b><span aria-hidden="true">→</span><b>{val(r.targetValue, r.unit)}</b></span></span>;
}

// ---------------------------------------------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------------------------------------------
export function FactoryHome() {
  const [tick, setTick] = useState(0);
  const o = useLoad(() => factory.overview(), [tick]);
  const d = o.data;
  return (
    <>
      <Bar status={d ? `${d.board.length} in the cycle · ${d.counts.roadmap ?? 0} on the roadmap` : "Loading…"}>
        <Link className="cmd primary" to="/factory/new">New request</Link>
        <Link className="cmd" to="/improve/roadmap">Roadmap</Link>
        <Link className="cmd" to="/factory/pipeline">Pipeline</Link>
        <Link className="cmd" to="/factory/docs">App documentation</Link>
        <Link className="cmd" to="/factory/tasks">Tasks</Link>
        <Link className="cmd" to="/improve/process-mining">Find issues with process mining</Link>
      </Bar>
      <div className="page">
        <div className="page-title"><h1>Implementation Factory</h1><span className="sub">Each request goes from its department's roadmap through the improvement cycle to a measured result and a new catalog cost. AI prepares the work. People make the decisions.</span></div>
        <ErrorBox error={o.error} />
        <div className="mx-kpis">
          <Link className="mx-kpi" to="/factory/requests"><span className="l">In the cycle</span><span className="v">{d?.board.length ?? "–"}</span><span className="s">{d ? `${d.counts.roadmap ?? 0} on the roadmap` : ""}</span></Link>
          <div className={`mx-kpi ${d?.waiting.length ? "warn" : ""}`}><span className="l">Waiting for you</span><span className="v">{d?.waiting.length ?? "–"}</span></div>
          <Link className="mx-kpi" to="/factory/tasks?view=review"><span className="l">Tasks at review</span><span className="v">{d?.counts ? d.waiting.filter((w) => w.kind === "approve_task").length : "–"}</span></Link>
          <div className="mx-kpi" title="Value a year of requests kept this quarter"><span className="l">Value proven, this quarter</span><span className="v">{d ? usd(d.value.proven) : "–"}</span><span className="s">{d ? `${d.value.provenCount} request${d.value.provenCount === 1 ? "" : "s"} kept` : ""}</span></div>
          <div className="mx-kpi"><span className="l">Value in progress</span><span className="v">{d ? usd(d.value.inProgress) : "–"}</span><span className="s">{d ? `${usd(d.value.proposed)} proposed` : ""}</span></div>
          <div className={`mx-kpi ${d && d.ai.spentUsd >= d.ai.limitUsd ? "hot" : ""}`}><span className="l">AI cost, this month</span><span className="v">{d ? usd(d.ai.spentUsd) : "–"}</span><span className="s">{d ? `of ${usd(d.ai.limitUsd)} limit` : ""}</span></div>
        </div>
        {d && d.stoppedAreas.length > 0 && <div className="msg warn">Changes are stopped in {d.stoppedAreas.includes("all") ? "all areas" : d.stoppedAreas.map(areaName).join(", ")}. Nobody can claim, send or deploy work there until an administrator starts them again below.</div>}

        <section className="card if-sec">
          <h2>Waiting for you</h2>
          {!d ? <div className="empty">Loading…</div> : !d.waiting.length ? <p className="muted" style={{ margin: 0 }}>Nothing is waiting for you.</p> : (
            <ul className="if-wait">{d.waiting.map((w, i) => (
              <li key={i}>{w.env && <EnvTag env={w.env} />}<Link to={w.task ? `/factory/t/${w.task}` : w.kind === "test" ? "/factory/testing" : w.kind === "fix" ? "/factory/testing?view=fix" : `/factory/r/${w.request}`}><b>{w.label}</b></Link><span className="t">{w.task ?? w.request} · {w.title}</span></li>))}
            </ul>)}
        </section>

        <section className="if-board" aria-label="Requests by step">
          {CYCLE.map((s, i) => {
            const cards = d?.board.filter((r) => (s.states as readonly string[]).includes(r.state)) ?? [];
            const info = d?.steps.find((x) => x.id === s.id);
            return (
              <div key={s.id} className="if-col">
                <div className="if-col-h"><b>{i + 1} {s.name}</b><span className="n">{cards.length}</span></div>
                <p className="if-col-d">{info?.does}</p>
                {cards.map((r) => (
                  <Link key={r.id} className="if-card" to={`/factory/r/${r.number}`}>
                    <span className="no">{r.number} · {r.pipeline ? "Change to an app" : r.projectTypeLabel}{r.state === "test" && " · testing"}</span>
                    <span className="ti">{r.title}</span>
                    <Measure r={r} />
                    {r.tasks.total > 0 && <span className="pr"><span className="mx-progress" style={{ margin: 0 }}><span style={{ width: `${(r.tasks.done / r.tasks.total) * 100}%` }} /></span>{r.tasks.done} of {r.tasks.total} tasks done</span>}
                  </Link>))}
              </div>);
          })}
        </section>

        <FactoryTimeline />

        <div className="split">
          <section className="card if-sec grow">
            <h2>Recent activity</h2>
            {!d?.recent.length ? <p className="muted" style={{ margin: 0 }}>No activity yet. <Link to="/factory/new">Write the first request</Link>.</p> : (
              <ul className="mx-hist">{d.recent.slice(0, 10).map((x, i) => (
                <li key={i}><span className="when">{ago(x.at)}</span><span><Link to={x.type === "factory_task" ? `/factory/t/${x.number}` : `/factory/r/${x.number}`}>{x.number}</Link> {x.reason && x.reason !== "created" ? x.reason : `${STATE_LABEL[x.to] ?? x.to}`}<span className="muted"> · {x.by ?? "–"}</span></span></li>))}
              </ul>)}
          </section>
          <SettingsCard onSaved={() => setTick((t) => t + 1)} />
        </div>
      </div>
    </>
  );
}

function SettingsCard({ onSaved }: { onSaved: () => void }) {
  const s = useLoad(() => factory.settings());
  const [limit, setLimit] = useState("");
  const [stopped, setStopped] = useState<string[]>([]);
  const [err, setErr] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { if (s.data) { setLimit(String(s.data.aiMonthlyUsd)); setStopped(s.data.stoppedAreas); } }, [s.data]);
  const save = async (e: FormEvent) => {
    e.preventDefault(); setErr(null); setMsg(null);
    try { await factory.saveSettings({ aiMonthlyUsd: Number(limit), stoppedAreas: stopped }); setMsg("Saved."); s.reload(); onSaved(); } catch (x) { setErr(x); }
  };
  const toggle = (id: string) => setStopped((x) => (x.includes(id) ? x.filter((a) => a !== id) : [...x, id]));
  return (
    <form className="card if-sec side" onSubmit={save}>
      <h2>Limits</h2>
      <div className="field"><label htmlFor="if-limit">Monthly AI cost limit (USD)</label>
        <input id="if-limit" className="input" type="number" min={0} step={50} value={limit} onChange={(e) => setLimit(e.target.value)} />
        <span className="hint">{s.data ? `${usd(s.data.spentUsd)} used this month. AI tools cannot claim tasks when the limit is reached.` : ""}</span></div>
      <fieldset className="if-stop"><legend className="label">Stop all changes</legend>
        <label className="mx-check"><input type="checkbox" checked={stopped.includes("all")} onChange={() => toggle("all")} /><span>In all areas</span></label>
        {!stopped.includes("all") && (
          <details><summary>In one area</summary>
            {AREAS.map((a) => <label key={a.id} className="mx-check"><input type="checkbox" checked={stopped.includes(a.id)} onChange={() => toggle(a.id)} /><span>{a.name}</span></label>)}
          </details>)}
      </fieldset>
      <ErrorBox error={err} />
      {msg && <div className="msg info">{msg}</div>}
      <button className="btn">Save limits</button>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------------------------------------------
const REQ_VIEWS: [string, string][] = [["open", "All open"], ["roadmap", "On the roadmap"], ...CYCLE_STATES.map((s) => [s, STATE_LABEL[s]] as [string, string]), ["closed", "Closed"], ["cancelled", "Cancelled"], ["all", "All"]];

export function RequestsPage() {
  const [sp, setSp] = useSearchParams();
  const view = sp.get("view") ?? "open";
  const type = sp.get("type") ?? "";
  const list = useLoad(() => factory.requests(view, type || undefined), [view, type]);
  return (
    <>
      <Bar status={list.data ? `${list.data.length} request${list.data.length === 1 ? "" : "s"}` : "Loading…"}><Link className="cmd primary" to="/factory/new">New request</Link></Bar>
      <div className="page">
        <div className="page-title"><h1>Requests</h1><span className="sub">On the roadmap and in the improvement cycle. Each request has a measure, a current value and a target</span></div>
        <ErrorBox error={list.error} />
        <div className="mx-desk">
          <nav className="mx-views" aria-label="Views">
            {REQ_VIEWS.map(([k, n]) => <button key={k} aria-current={view === k} onClick={() => setSp(k === "open" ? (type ? { type } : {}) : { view: k, ...(type ? { type } : {}) })}><span>{n}</span></button>)}
          </nav>
          <section className="card">
            <div className="filters" style={{ borderBottom: "1px solid var(--stroke)" }}>
              <label className="muted" htmlFor="if-type">Project type</label>
              <select id="if-type" className="select" value={type} onChange={(e) => setSp({ ...(view !== "open" ? { view } : {}), ...(e.target.value ? { type: e.target.value } : {}) })} style={{ maxWidth: 240 }}>
                <option value="">All</option><option value="improvement">Improvement projects</option><option value="product">Product changes</option><option value="implementation">Implementation projects</option>
              </select>
            </div>
            {!list.data ? <div className="empty">Loading…</div> : !list.data.length ? <div className="empty">No requests here. <Link to="/factory/new">Write one</Link>.</div> : (
              <div className="table-wrap"><table className="dgrid">
                <thead><tr><th>Number</th><th>Request</th><th>Step</th><th className="mx-hide-m">Measure</th><th className="mx-hide-m">Value a year</th><th className="mx-hide-m">Updated</th></tr></thead>
                <tbody>{list.data.map((r) => (
                  <tr key={r.id}>
                    <td><Link className="num" to={`/factory/r/${r.number}`}>{r.number}</Link></td>
                    <td className="title" title={r.title}>{r.title}<span className="sub">{[r.projectTypeLabel, r.area && areaName(r.area)].filter(Boolean).join(" · ")}</span></td>
                    <td><Chip state={r.state} />{r.decision && <span className="muted"> {DECISION_LABEL[r.decision]}</span>}</td>
                    <td className="mx-hide-m"><Measure r={r} /></td>
                    <td className="mx-hide-m">{r.valuePerYear === null ? <span className="muted">–</span> : usd(r.valuePerYear)}</td>
                    <td className="muted mx-hide-m" style={{ whiteSpace: "nowrap" }}>{ago(r.updatedAt)}</td>
                  </tr>))}
                </tbody>
              </table></div>)}
          </section>
        </div>
      </div>
    </>
  );
}

export function NewRequestPage() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const insightId = sp.get("insight") ?? undefined;
  const roadmap = sp.get("start") === "roadmap";
  const type = (sp.get("type") ?? "improvement") as RequestRow["projectType"];
  const issue = sp.get("issue") ?? undefined;
  const catalog = useLoad(() => imp.processes().catch(() => null));
  const [b, setB] = useState({ title: "", area: sp.get("area") ?? "" });
  const [proc, setProc] = useState<ProcessValue>({ process: sp.get("process") ?? "", step: sp.get("step") ?? "" });
  // Every improvement works on a process in the catalog; a product change or an implementation project may not.
  const needsProcess = type === "improvement";
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const num = (k: string) => { const v = sp.get(k); return v === null || v.trim() === "" ? undefined : Number(v); };
  const picked = catalog.data?.processes.find((x) => x.number === proc.process);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      const processId = await settleProcess(proc);
      // The rest comes from the process (the measure and where it is now), from the page that opened this (the cost
      // calculator's target and value, a process mining finding), or is written on the A3 once it's saved.
      const r = await factory.create({
        projectType: type, title: b.title || undefined, area: (picked?.area ?? (proc.process === "__new__" ? proc.newArea : null) ?? b.area) || undefined,
        processId, step: proc.step || undefined, insightId, issue,
        start: roadmap ? "roadmap" : "now", measure: sp.get("measure") || undefined, unit: sp.get("unit") || undefined,
        currentValue: num("current"), targetValue: num("target"), valuePerYear: num("value"),
      });
      nav(`/factory/r/${r.number}`);
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  const flip = (k: string, v: string | null) => { const next = new URLSearchParams(sp); if (v === null) next.delete(k); else next.set(k, v); setSp(next, { replace: true }); };
  return (
    <><Bar />
      <div className="page mx-form">
        <div className="page-title"><h1>{type === "product" ? "New product change" : type === "implementation" ? "New implementation project" : "New improvement"}</h1>
          <span className="sub">A title, the process it improves and, if it's one step, the step. You write the rest on its A3.</span></div>
        <form className="card a3-new" style={{ padding: 20, maxWidth: 720 }} onSubmit={submit}>
          {insightId && <div className="msg info">From a process mining finding. Where it is now comes from the finding, and the result is measured with the same number.</div>}
          {issue && <div className="msg info">From {issue}: it improves the process the issue happened in.</div>}
          <div className="field"><label htmlFor="nr-title">What do you want to improve?</label>
            <input id="nr-title" className="input" required={!insightId && !issue} minLength={3} autoFocus placeholder={insightId || issue ? "Leave empty to use its title" : "e.g. Decrease the MRB wait time"} value={b.title} onChange={(e) => setB({ ...b, title: e.target.value })} /></div>
          <ProcessPicker value={proc} onChange={setProc} required={needsProcess} allowNew idPrefix="nr" area={b.area || null} />
          {!proc.process && !needsProcess && <div className="field"><label htmlFor="nr-area">Department</label><select id="nr-area" className="select" value={b.area} onChange={(e) => setB({ ...b, area: e.target.value })}><option value="">Not one department</option>{AREAS.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>}
          <ErrorBox error={err} />
          <div className="btn-row"><button className="btn primary" disabled={busy}>{roadmap ? "Add to the roadmap" : "Start the A3"}</button><Link className="btn" to={roadmap ? "/improve/roadmap" : "/factory"}>Cancel</Link></div>
          <p className="hint" style={{ marginTop: 12 }}>
            {roadmap ? <>It goes on the department's roadmap, ranked by value a year. <button type="button" className="linkish" onClick={() => flip("start", null)}>Start it now instead</button>.</>
              : <>It starts at Plan. <button type="button" className="linkish" onClick={() => flip("start", "roadmap")}>Put it on the roadmap instead</button>.</>}
            {type === "improvement" && <> Changing the CourageMFG software? <button type="button" className="linkish" onClick={() => flip("type", "product")}>Make a product change</button>.</>}
          </p>
        </form>
      </div>
    </>
  );
}

export function RequestPage() {
  const { number = "" } = useParams();
  const [tick, setTick] = useState(0);
  const r = useLoad(() => factory.request(number), [number, tick]);
  const rules = useLoad(() => factory.rules());
  const [err, setErr] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setErr(null); setNote(null);
    try { await fn(); setNote(done ?? null); setTick((t) => t + 1); return true; } catch (x) { setErr(x); return false; }
  };
  const d = r.data;
  if (r.error) return <div className="page"><ErrorBox error={r.error} /><Link to="/factory">Back to the Implementation Factory</Link></div>;
  if (!d) return <div className="page"><div className="empty">Loading…</div></div>;
  if (d.lean) return <LeanRequest d={d} act={act} note={note} err={err} reload={() => setTick((t) => t + 1)} />;
  const ask = (q: string) => { const v = prompt(q); return v && v.trim() ? v.trim() : null; };
  const latest = d.designs[0];
  const live = d.tasks.filter((t) => t.state !== "cancelled");
  const canEdit = ["roadmap", "define", "design"].includes(d.state);
  const pipeline = d.pipeline;
  return (
    <>
      <Bar status={<>{d.number} · <Chip state={d.state} /></>}>
        {d.state === "roadmap" && <button className="cmd primary" onClick={() => act(() => factory.start(d.number), "Started. It's at Simplify: take out the waste first, then approve the request.")}>Start</button>}
        {d.state === "define" && <button className="cmd primary" onClick={() => act(() => factory.approve(d.number), "Request approved. Map the future state and list the tasks.")}>Approve request</button>}
        {d.state === "design" && <button className="cmd primary" disabled={!latest || !live.length} onClick={() => act(() => factory.approveDesign(d.number), `Design version ${latest?.version} approved. The tasks are ready.`)}>Approve design</button>}
        {["build", "test", "deploy"].includes(d.state) && <button className="cmd" onClick={() => { const why = ask("Why must the design change?"); if (why) act(() => factory.reopenDesign(d.number, why), "The design is open again. Unfinished tasks are back to draft."); }}>Reopen design</button>}
        {["roadmap", "define", "design", "build", "test", "deploy"].includes(d.state) && <button className="cmd" onClick={() => { const why = ask("Why cancel this request?"); if (why) act(() => factory.cancel(d.number, why), "Cancelled."); }}>Cancel request</button>}
        <Link className="cmd" to={`/factory/tasks?request=${d.number}&view=all`}>All tasks</Link>
      </Bar>
      <div className="page">
        <div className="page-title"><h1>{d.title}</h1><span className="sub">{d.number} · {pipeline ? "A change to an app" : d.projectTypeLabel}{d.area && ` · ${areaName(d.area)}`}{pipeline && d.tier === "high" && " · High risk"}{d.decision && ` · ${DECISION_LABEL[d.decision]}`}</span></div>
        {!pipeline && <Steps state={d.state} />}
        {d.state === "roadmap" && <div className="msg info">On {areaName(d.area) === "–" ? "the" : `${areaName(d.area)}'s`} <Link to="/improve/roadmap">roadmap</Link>, not started. A person starts it when the department is ready.</div>}
        <ErrorBox error={err} />
        {note && <div className="msg info">{note}</div>}
        {pipeline && <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 12 }}><RequestPipeline number={d.number} onChange={() => setTick((t) => t + 1)} /></div>}
        {d.timeline && <RequestTimeline d={d} />}
        <div className="split">
          <div className="grow" style={{ gap: 12 }}>
            <RequestCard d={d} canEdit={canEdit} act={act} />
            {!pipeline && <NumberCard record={d.number} onRecordResult={d.state === "measure" ? async (value) => { await act(() => factory.result(d.number, { value, note: "From the dashboard number" }), "Result recorded from the dashboard.") } : undefined} />}
            <DesignCard d={d} canEdit={canEdit} act={act} />
            <TasksCard d={d} canEdit={canEdit} act={act} changeTypes={rules.data?.changeTypes ?? {}} />
            {d.state === "deploy" && !pipeline && <DeployCard d={d} act={act} />}
            {(["measure", "recatalog", "closed"].includes(d.state) || d.results.length > 0) && (!pipeline || d.measure) && <ResultsCard d={d} act={act} />}
            {d.state === "recatalog" && <RecatalogCard d={d} act={act} />}
            {d.recataloged && <RecatalogedCard d={d} />}
          </div>
          <aside className="side">
            <section className="card mx-sec"><h2>Details</h2>
              <dl className="mx-dl">
                <dt>Step</dt><dd><Chip state={d.state} /></dd>
                <dt>Type</dt><dd>{pipeline ? "A change to an app" : d.projectTypeLabel}</dd>
                {pipeline && <><dt>Risk</dt><dd>{d.tier === "high" ? "High: the process owner and an administrator approve the release" : "Standard: the process owner approves the release"}</dd></>}
                <dt>Area</dt><dd>{areaName(d.area)}</dd>
                <dt>From</dt><dd>{d.insight ? <Link to="/improve/process-mining?view=accepted">Process mining</Link> : d.source === "agent" ? "An AI tool" : d.source === "chat" ? "Courage Chat" : d.source === "issue" ? "An issue" : "A person"}</dd>
                <dt>Process</dt><dd>{d.process ? <Link to={`/improve/catalog/${d.process.number}`}>{d.process.number} {d.process.name}</Link> : "Not in the catalog"}</dd>
                <dt>Value a year</dt><dd>{d.valuePerYear === null ? "–" : usd(d.valuePerYear)}</dd>
                <dt>Approved</dt><dd>{d.approvedAt ? when(d.approvedAt) : "Not yet"}</dd>
              </dl>
            </section>
            {d.approvals.length > 0 && (
              <section className="card mx-sec"><h2>Decisions</h2>
                <ul className="mx-hist">{d.approvals.map((a, i) => (
                  <li key={i}><span className="when">{when(a.at)}</span><span>{a.targetKind === "start" ? "Started" : <>{a.decision === "approve" ? "Approved" : "Rejected"} {a.targetKind === "design" ? `design version ${a.designVersion}` : a.targetKind}</>}{a.reason ? `: ${a.reason}` : ""}<span className="muted"> · {a.byName ?? a.by}{a.role ? `, ${a.role}` : ""}</span></span></li>))}
                </ul>
              </section>)}
            {d.deployments.length > 0 && (
              <section className="card mx-sec"><h2>Deployments</h2>
                <ul className="mx-hist">{d.deployments.map((x) => (
                  <li key={x.id}><span className="when">{when(x.deployedAt)}</span><span><b>{x.number}</b> · rollback point {x.rollbackPoint}{x.rolledBackAt && <span className="if-chip warn" style={{ marginLeft: 6 }}>Rolled back</span>}{x.notes && <span className="muted"> · {x.notes}</span>}</span></li>))}
                </ul>
              </section>)}
            <section className="card mx-sec"><h2>History</h2>
              <ul className="mx-hist">{d.history.slice().reverse().map((h, i) => (
                <li key={i}><span className="when">{when(h.at)}</span><span>{h.reason && h.reason !== "created" ? h.reason : STATE_LABEL[h.toState] ?? h.toState}<span className="muted"> · {h.actorName ?? "–"}</span></span></li>))}
              </ul>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}

type Act = (fn: () => Promise<unknown>, done?: string) => Promise<boolean>;

function RequestCard({ d, canEdit, act }: { d: RequestDetail; canEdit: boolean; act: Act }) {
  const [edit, setEdit] = useState(false);
  const [b, setB] = useState({ title: d.title, problem: d.problem, cause: d.cause, measure: d.measure ?? "", unit: d.unit ?? "", better: d.better, currentValue: d.currentValue?.toString() ?? "", targetValue: d.targetValue?.toString() ?? "", valuePerYear: d.valuePerYear?.toString() ?? "" });
  const num = (s: string) => (s.trim() === "" ? null : Number(s));
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (await act(() => factory.update(d.number, { ...b, better: b.better as "lower" | "higher", currentValue: num(b.currentValue), targetValue: num(b.targetValue), valuePerYear: num(b.valuePerYear) }), "Saved.")) setEdit(false);
  };
  const set = (k: keyof typeof b) => (e: { target: { value: string } }) => setB({ ...b, [k]: e.target.value });
  const missing = d.state === "define" && !d.pipeline ? [!d.measure && "a measure", d.currentValue === null && "the current value", d.targetValue === null && "a target"].filter(Boolean) : [];
  return (
    <section className="card">
      <div className="mx-sec">
        <h2>Request{canEdit && !edit && <button className="btn act" onClick={() => setEdit(true)}>Edit</button>}</h2>
        {edit ? (
          <form onSubmit={save}>
            <div className="field"><label htmlFor="rq-t">Title</label><input id="rq-t" className="input" value={b.title} onChange={set("title")} /></div>
            <div className="field"><label htmlFor="rq-p">Problem</label><textarea id="rq-p" className="textarea" rows={3} value={b.problem} onChange={set("problem")} /></div>
            <div className="field"><label htmlFor="rq-c">Cause</label><textarea id="rq-c" className="textarea" rows={2} value={b.cause} onChange={set("cause")} /></div>
            <div className="form-grid">
              <div className="field"><label htmlFor="rq-m">Measure</label><input id="rq-m" className="input" value={b.measure} onChange={set("measure")} /></div>
              <div className="field"><label htmlFor="rq-u">Unit</label><input id="rq-u" className="input" value={b.unit} onChange={set("unit")} /></div>
              <div className="field"><label htmlFor="rq-b">Better is</label><select id="rq-b" className="select" value={b.better} onChange={set("better")}><option value="lower">Lower</option><option value="higher">Higher</option></select></div>
              <div className="field"><label htmlFor="rq-cv">Current value</label><input id="rq-cv" className="input" type="number" step="any" value={b.currentValue} onChange={set("currentValue")} /></div>
              <div className="field"><label htmlFor="rq-tv">Target value</label><input id="rq-tv" className="input" type="number" step="any" value={b.targetValue} onChange={set("targetValue")} /></div>
              <div className="field"><label htmlFor="rq-v">Value a year (USD)</label><input id="rq-v" className="input" type="number" step="any" value={b.valuePerYear} onChange={set("valuePerYear")} /></div>
            </div>
            <div className="btn-row"><button className="btn primary">Save</button><button type="button" className="btn" onClick={() => setEdit(false)}>Cancel</button></div>
          </form>
        ) : (
          <>
            {(!d.pipeline || d.measure) && <div className="if-target">
              <div><span className="l">Measure</span><b>{d.measure ?? "–"}</b></div>
              <div><span className="l">Current value</span><b>{val(d.currentValue, d.unit)}</b></div>
              <div><span className="l">Target</span><b>{val(d.targetValue, d.unit)}</b><span className="s">{d.better === "higher" ? "Higher is better" : "Lower is better"}</span></div>
            </div>}
            {d.problem ? <p style={{ whiteSpace: "pre-wrap", margin: "12px 0 0" }}>{d.problem}</p> : <p className="muted" style={{ margin: "12px 0 0" }}>No problem statement yet.</p>}
            {d.cause && <p style={{ whiteSpace: "pre-wrap", margin: "8px 0 0" }}><b>Cause.</b> {d.cause}</p>}
            {d.insight && <p className="hint" style={{ margin: "8px 0 0" }}>From process mining: {d.insight.title}. Now {val(d.insight.metric.value, d.insight.metric.unit)}.</p>}
            {missing.length > 0 && <div className="msg warn" style={{ margin: "12px 0 0" }}>Before approval, add {missing.join(", ")}.</div>}
          </>
        )}
      </div>
    </section>
  );
}

function DesignCard({ d, canEdit, act }: { d: RequestDetail; canEdit: boolean; act: Act }) {
  const [open, setOpen] = useState(false);
  const latest = d.designs[0];
  const [body, setBody] = useState("");
  const [acc, setAcc] = useState("");
  const start = () => { setBody(latest?.body ?? ""); setAcc((latest?.acceptance ?? []).join("\n")); setOpen(true); };
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (await act(() => factory.addDesign(d.number, body, acc.split("\n").map((x) => x.trim()).filter(Boolean)), "New design version saved.")) setOpen(false);
  };
  return (
    <section className="card mx-sec">
      <h2>Future state {latest && <span className="muted">version {latest.version}{latest.approvedAt ? ", approved" : ", not approved"}{latest.createdVia === "mcp" ? " · written by an AI tool" : ""}</span>}
        {canEdit && !open && <button className="btn act" onClick={start}>{latest ? "New version" : "Write the design"}</button>}</h2>
      {open ? (
        <form onSubmit={save}>
          <div className="field"><label htmlFor="ds-b">How the process will run: the steps, the setup and the data changes</label><textarea id="ds-b" className="textarea" rows={8} value={body} onChange={(e) => setBody(e.target.value)} /></div>
          <div className="field"><label htmlFor="ds-a">Acceptance criteria</label><textarea id="ds-a" className="textarea" rows={3} placeholder="One per line" value={acc} onChange={(e) => setAcc(e.target.value)} /></div>
          <div className="btn-row"><button className="btn primary">Save version {(latest?.version ?? 0) + 1}</button><button type="button" className="btn" onClick={() => setOpen(false)}>Cancel</button></div>
        </form>
      ) : latest ? (
        <>
          <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{latest.body}</p>
          {latest.acceptance.length > 0 && <><h3 className="if-h3">Acceptance criteria</h3><ul className="if-list">{latest.acceptance.map((a, i) => <li key={i}>{a}</li>)}</ul></>}
        </>
      ) : <p className="muted" style={{ margin: 0 }}>{d.state === "roadmap" ? "Start the request, then map the future state." : d.state === "define" ? "Approve the request, then map the future state." : "No design yet."}</p>}
    </section>
  );
}

function TasksCard({ d, canEdit, act, changeTypes }: { d: RequestDetail; canEdit: boolean; act: Act; changeTypes: Record<string, { label: string; ai: boolean; approvals: number; roles: string[] }> }) {
  const [open, setOpen] = useState(false);
  const blank: NewTask = { title: "", changeType: "setup_change", doneBy: "ai", description: "", tests: "", rollback: "" };
  const [t, setT] = useState<NewTask>(blank);
  const save = async (e: FormEvent) => { e.preventDefault(); if (await act(() => factory.addTasks(d.number, [t]), "Task added.")) { setT(blank); setOpen(false); } };
  const ct = changeTypes[t.changeType ?? "setup_change"];
  return (
    <section className="card">
      <div className="mx-sec" style={{ paddingBottom: d.tasks.length ? 8 : 16 }}>
        <h2>Tasks <span className="muted">{d.tasks.filter((x) => ["approved", "deployed"].includes(x.state)).length} of {d.tasks.filter((x) => x.state !== "cancelled").length} done</span>
          {canEdit && !open && <button className="btn act" onClick={() => setOpen(true)}>Add a task</button>}</h2>
        {open && (
          <form onSubmit={save} className="if-addtask">
            <div className="field"><label htmlFor="tk-t">Task</label><input id="tk-t" className="input" required value={t.title} onChange={(e) => setT({ ...t, title: e.target.value })} /></div>
            <div className="form-grid">
              <div className="field"><label htmlFor="tk-c">Change type</label><select id="tk-c" className="select" value={t.changeType} onChange={(e) => setT({ ...t, changeType: e.target.value, doneBy: changeTypes[e.target.value]?.ai === false ? "person" : t.doneBy })}>
                {Object.entries(changeTypes).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
                {ct && <span className="hint">{ct.approvals ? `Approved by ${ct.roles.join(" and ")}.` : "No approval needed."}</span>}</div>
              <div className="field"><label htmlFor="tk-d">Done by</label><select id="tk-d" className="select" value={t.doneBy} disabled={ct?.ai === false} onChange={(e) => setT({ ...t, doneBy: e.target.value as "ai" | "person" })}><option value="ai">AI</option><option value="person">A person</option></select>
                {ct?.ai === false && <span className="hint">Only a person can do this change type.</span>}</div>
            </div>
            <div className="field"><label htmlFor="tk-desc">What to do</label><textarea id="tk-desc" className="textarea" rows={2} value={t.description} onChange={(e) => setT({ ...t, description: e.target.value })} /></div>
            <div className="form-grid">
              <div className="field"><label htmlFor="tk-test">How to test it</label><input id="tk-test" className="input" value={t.tests} onChange={(e) => setT({ ...t, tests: e.target.value })} /></div>
              <div className="field"><label htmlFor="tk-rb">How to undo it</label><input id="tk-rb" className="input" value={t.rollback} onChange={(e) => setT({ ...t, rollback: e.target.value })} /></div>
            </div>
            <div className="btn-row"><button className="btn primary">Add task</button><button type="button" className="btn" onClick={() => setOpen(false)}>Cancel</button></div>
          </form>)}
        {!d.tasks.length && !open && <p className="muted" style={{ margin: 0 }}>No tasks yet.</p>}
      </div>
      {d.tasks.length > 0 && <TaskTable tasks={d.tasks} showRequest={false} />}
    </section>
  );
}

function who(t: Task, full = false) {
  if (!t.claimedBy) return <span className="muted">{t.doneBy === "ai" ? "For AI" : "For a person"}</span>;
  return t.claimedVia === "mcp" ? <span title={`For ${t.claimedByName ?? "–"}`}>{t.agent ?? "AI tool"}{full && <span className="muted"> for {t.claimedByName ?? "–"}</span>}</span> : <span>{t.claimedByName ?? "–"}</span>;
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function TaskTable({ tasks, showRequest }: { tasks: Task[]; showRequest: boolean }) {
  return (
    <div className="table-wrap"><table className="dgrid">
      <thead><tr><th>Number</th><th>Task</th>{showRequest && <th className="mx-hide-m">Request</th>}<th>Status</th><th className="mx-hide-m">Done by</th><th className="mx-hide-m">Approvals</th><th className="mx-hide-m">Tests</th></tr></thead>
      <tbody>{tasks.map((t) => {
        const failing = t.testResults.filter((x) => !x.passed).length;
        return (
          <tr key={t.id}>
            <td><Link className="num" to={`/factory/t/${t.number}`}>{t.number}</Link></td>
            <td className="title" title={t.title}>{t.title}<span className="sub">{t.changeTypeLabel}</span></td>
            {showRequest && <td className="mx-hide-m"><Link to={`/factory/r/${t.request.number}`}>{t.request.number}</Link></td>}
            <td><Chip state={t.state} /></td>
            <td className="mx-hide-m" style={{ whiteSpace: "nowrap" }}>{who(t)}</td>
            <td className="mx-hide-m">{t.approvalsNeeded === 0 ? <span className="muted">Not needed</span> : t.state === "review" ? `${t.approvals.length} of ${t.approvalsNeeded}` : ["approved", "deployed"].includes(t.state) ? "Done" : <span className="muted">–</span>}</td>
            <td className="mx-hide-m">{!t.testResults.length ? <span className="muted">–</span> : failing ? <span className="if-chip danger">{failing} failing</span> : <span className="if-chip ok">{t.testResults.length} passed</span>}</td>
          </tr>);
      })}</tbody>
    </table></div>
  );
}

function DeployCard({ d, act }: { d: RequestDetail; act: Act }) {
  const [rp, setRp] = useState("");
  const [notes, setNotes] = useState("");
  return (
    <form className="card mx-sec if-callout" onSubmit={(e) => { e.preventDefault(); act(() => factory.deploy(d.number, rp, notes), "Deployed. The go-live value is recorded. Record the result at 7, 30 and 90 days."); }}>
      <h2>Deploy</h2>
      <p style={{ margin: "0 0 12px" }}>All {d.tasks.filter((t) => t.state === "approved").length} tasks are approved. Record the rollback point, then put the changes live.</p>
      <div className="field"><label htmlFor="dp-rp">Rollback point</label><input id="dp-rp" className="input" required placeholder="The Neon restore point, the git commit or the previous setup version" value={rp} onChange={(e) => setRp(e.target.value)} /></div>
      <div className="field"><label htmlFor="dp-n">Notes</label><input id="dp-n" className="input" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
      <button className="btn primary">Deploy</button>
    </form>
  );
}

function ResultsCard({ d, act }: { d: RequestDetail; act: Act }) {
  const [day, setDay] = useState<string>(d.resultsDue[0]?.toString() ?? "");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [decision, setDecision] = useState("");
  const [reason, setReason] = useState("");
  const record = (e: FormEvent) => { e.preventDefault(); act(() => factory.result(d.number, { day: day === "" ? null : Number(day), value: value.trim() === "" ? null : Number(value), note }), "Result recorded.").then((ok) => { if (ok) { setValue(""); setNote(""); } }); };
  const recat = d.projectType !== "product";
  const decide = (e: FormEvent) => { e.preventDefault(); act(() => factory.decide(d.number, decision, reason), decision === "keep" ? (recat ? "Kept. Re-catalog the process: its new numbers go back into the catalog." : "Kept. The change is standard work.") : "Back to Map future state."); };
  const recorded = d.results.map((r) => r.day);
  return (
    <section className="card">
      <div className="mx-sec">
        <h2>Results <span className="muted">{d.measure} · target {val(d.targetValue, d.unit)}</span></h2>
        {d.resultsDue.length > 0 && <div className="msg warn">The {d.resultsDue.join(", ")}-day result{d.resultsDue.length > 1 ? "s are" : " is"} due.</div>}
        {d.results.length > 0 && (
          <div className="table-wrap"><table className="dgrid">
            <thead><tr><th>When</th><th>Value</th><th>Target met</th><th className="mx-hide-m">From</th><th className="mx-hide-m">Note</th></tr></thead>
            <tbody>{d.results.map((r) => (
              <tr key={r.id}><td>{r.day === null ? when(r.at) : r.day === 0 ? "Go-live" : `Day ${r.day}`}</td><td><b>{val(r.value, d.unit)}</b></td>
                <td>{r.meetsTarget === null ? "–" : r.meetsTarget ? <span className="if-chip ok">Yes</span> : <span className="if-chip warn">Not yet</span>}</td>
                <td className="mx-hide-m muted">{r.source === "process_mining" ? "Process mining" : "A person"}</td><td className="mx-hide-m muted">{r.note}</td></tr>))}
            </tbody>
          </table></div>)}
      </div>
      {["measure", "recatalog", "closed"].includes(d.state) && (
        <form className="mx-sec" onSubmit={record}>
          <div className="form-grid">
            <div className="field"><label htmlFor="rs-d">Result for</label><select id="rs-d" className="select" value={day} onChange={(e) => setDay(e.target.value)}>
              <option value="">A reading now</option>{[7, 30, 90].filter((x) => !recorded.includes(x)).map((x) => <option key={x} value={x}>Day {x}</option>)}</select></div>
            <div className="field"><label htmlFor="rs-v">Value{d.unit ? ` (${d.unit})` : ""}</label><input id="rs-v" className="input" type="number" step="any" placeholder={d.insight ? "Leave empty to use process mining" : ""} value={value} onChange={(e) => setValue(e.target.value)} /></div>
            <div className="field"><label htmlFor="rs-n">Note</label><input id="rs-n" className="input" value={note} onChange={(e) => setNote(e.target.value)} /></div>
          </div>
          <button className="btn">Record the result</button>
        </form>)}
      {d.state === "measure" && d.results.length > 0 && (
        <form className="mx-sec" onSubmit={decide}>
          <h2>Decide</h2>
          <div className="mx-big-choice if-choice3">
            <label><input type="radio" name="dc" checked={decision === "keep"} onChange={() => setDecision("keep")} /><span><b>Keep</b><span>{recat ? "The change is standard work. Next, re-catalog the process." : "The change is standard work. The request closes."}</span></span></label>
            <label><input type="radio" name="dc" checked={decision === "change_again"} onChange={() => setDecision("change_again")} /><span><b>Change again</b><span>Keep the change and go back to Map future state for more.</span></span></label>
            <label><input type="radio" name="dc" checked={decision === "roll_back"} onChange={() => setDecision("roll_back")} /><span><b>Roll back</b><span>You undo the change with its rollback point. The request goes back to Map future state.</span></span></label>
          </div>
          {decision && decision !== "keep" && <div className="field" style={{ marginTop: 12 }}><label htmlFor="dc-r">Why</label><input id="dc-r" className="input" required value={reason} onChange={(e) => setReason(e.target.value)} /></div>}
          <button className="btn primary" disabled={!decision} style={{ marginTop: 12 }}>Record the decision</button>
        </form>)}
    </section>
  );
}

/** Re-catalog: the process's new numbers and the systems it no longer needs go back into the catalog. */
function RecatalogCard({ d, act }: { d: RequestDetail; act: Act }) {
  const catalog = useLoad(() => imp.processes());
  const [pick, setPick] = useState(d.process?.number ?? "");
  const proc = useLoad(() => (pick ? imp.process(pick) : Promise.resolve(null)), [pick]);
  const [b, setB] = useState({ volumePerMonth: "", minutesEach: "", note: "" });
  const [retire, setRetire] = useState<string[]>([]);
  const p = proc.data;
  useEffect(() => { if (p) setB((x) => ({ ...x, volumePerMonth: p.volumePerMonth?.toString() ?? "", minutesEach: p.minutesEach?.toString() ?? "" })); setRetire([]); }, [p?.id]);
  const n = (v: string) => (v.trim() === "" ? null : Number(v));
  const costEach = p && n(b.minutesEach) !== null ? (n(b.minutesEach)! / 60) * p.hourlyCost : null;
  const costYear = costEach !== null && n(b.volumePerMonth) !== null ? Math.round(costEach * n(b.volumePerMonth)! * 12) : null;
  const save = (e: FormEvent) => {
    e.preventDefault();
    act(() => factory.recatalog(d.number, pick ? { processId: pick, volumePerMonth: n(b.volumePerMonth), minutesEach: n(b.minutesEach), retireSystems: retire, note: b.note || undefined } : { note: b.note }),
      pick ? "Re-cataloged. The process's new numbers are in the catalog, and the request is closed." : "Closed with no catalog change.");
  };
  return (
    <form className="card mx-sec if-callout" onSubmit={save}>
      <h2>Re-catalog</h2>
      <p style={{ margin: "0 0 12px" }}>The change is kept. Put the process's new numbers into the catalog, and retire the systems it no longer needs. The next improvement starts from these numbers.</p>
      <div className="field"><label htmlFor="rc-p">Process</label>
        <select id="rc-p" className="select" value={pick} onChange={(e) => setPick(e.target.value)}>
          <option value="">No process in the catalog changes</option>
          {catalog.data?.processes.map((x) => <option key={x.number} value={x.number}>{x.number} · {x.name}</option>)}
        </select></div>
      {pick && p && <>
        <div className="form-grid">
          <div className="field"><label htmlFor="rc-v">Times a month</label><input id="rc-v" className="input" type="number" min={0} step="any" value={b.volumePerMonth} onChange={(e) => setB({ ...b, volumePerMonth: e.target.value })} /><span className="hint">Was {p.volumePerMonth ?? "–"}</span></div>
          <div className="field"><label htmlFor="rc-m">Minutes each</label><input id="rc-m" className="input" type="number" min={0} step="any" value={b.minutesEach} onChange={(e) => setB({ ...b, minutesEach: e.target.value })} /><span className="hint">Was {p.minutesEach ?? "–"}, all people together</span></div>
        </div>
        <div className="if-target" style={{ marginBottom: 12 }}>
          <div><span className="l">Cost a year now</span><b>{p.costPerYear === null ? "–" : usd(p.costPerYear)}</b></div>
          <div><span className="l">After</span><b>{costYear === null ? "–" : usd(costYear)}</b>{costYear !== null && p.costPerYear !== null && <span className="s">{costYear <= p.costPerYear ? `${usd(p.costPerYear - costYear)} less a year` : `${usd(costYear - p.costPerYear)} more a year`}</span>}</div>
          <div><span className="l">Cost an hour</span><b>{usd(p.hourlyCost)}</b></div>
        </div>
        {p.systems.filter((x) => !x.retiredAt).length > 0 && (
          <fieldset style={{ border: 0, padding: 0, margin: "0 0 12px" }}><legend className="label" style={{ fontWeight: 600, marginBottom: 6 }}>Systems it no longer needs</legend>
            {p.systems.filter((x) => !x.retiredAt).map((x) => (
              <label key={x.id} className="mx-check"><input type="checkbox" checked={retire.includes(x.id)} onChange={(e) => setRetire(e.target.checked ? [...retire, x.id] : retire.filter((y) => y !== x.id))} />
                <span>Retire {x.name}{x.costPerYear ? <span className="muted"> · {usd(x.costPerYear)} a year</span> : null}</span></label>))}
          </fieldset>)}
      </>}
      <div className="field"><label htmlFor="rc-n">{pick ? "Note" : "Why no process changes"}</label><input id="rc-n" className="input" required={!pick} placeholder={pick ? "e.g. Measured on 40 NCRs in October" : "e.g. A 5S change: no process numbers move"} value={b.note} onChange={(e) => setB({ ...b, note: e.target.value })} /></div>
      <ErrorBox error={catalog.error ?? proc.error} />
      <button className="btn primary">{pick ? "Re-catalog and close" : "Close with no catalog change"}</button>
    </form>
  );
}

function RecatalogedCard({ d }: { d: RequestDetail }) {
  const r = d.recataloged!;
  return (
    <section className="card mx-sec">
      <h2>Re-cataloged <span className="muted">{when(r.at)}{r.byName ? ` · ${r.byName}` : ""}</span></h2>
      <div className="if-target">
        <div><span className="l">Minutes each</span><b>{r.before?.minutesEach ?? "–"} → {r.after.minutesEach ?? "–"}</b></div>
        <div><span className="l">Cost each</span><b>{r.before?.costEach == null ? "–" : usd(r.before.costEach)} → {r.after.costEach === null ? "–" : usd(r.after.costEach)}</b></div>
        <div><span className="l">Cost a year</span><b>{r.before?.costPerYear == null ? "–" : usd(r.before.costPerYear)} → {r.after.costPerYear === null ? "–" : usd(r.after.costPerYear)}</b></div>
      </div>
      {r.systemsRetired.length > 0 && <p style={{ margin: "12px 0 0" }}>Retired: {r.systemsRetired.join(", ")}.</p>}
      {r.note && <p className="muted" style={{ margin: "8px 0 0" }}>{r.note}</p>}
      {d.process && <p style={{ margin: "8px 0 0" }}><Link to={`/improve/catalog/${d.process.number}`}>Open {d.process.number} in the catalog</Link></p>}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------------------------------------------
const TASK_VIEWS: [string, string][] = [["review", "At review"], ["ready", "Ready"], ["in_progress", "In progress"], ["mine", "Mine"], ["open", "All open"], ["approved", "Approved"], ["deployed", "Deployed"], ["all", "All"]];

export function TasksPage() {
  const [sp, setSp] = useSearchParams();
  const view = sp.get("view") ?? "review";
  const request = sp.get("request") ?? undefined;
  const list = useLoad(() => factory.tasks(view === "mine" ? { state: "all", mine: true, request } : { state: view, request }), [view, request]);
  return (
    <>
      <Bar status={list.data ? `${list.data.length} task${list.data.length === 1 ? "" : "s"}` : "Loading…"} />
      <div className="page">
        <div className="page-title"><h1>Tasks{request && <> on <Link to={`/factory/r/${request}`}>{request}</Link></>}</h1><span className="sub">AI and people do tasks in a test copy. A person approves each change.</span></div>
        <ErrorBox error={list.error} />
        <div className="mx-desk">
          <nav className="mx-views" aria-label="Views">
            {TASK_VIEWS.map(([k, n]) => <button key={k} aria-current={view === k} onClick={() => setSp({ ...(k !== "review" ? { view: k } : {}), ...(request ? { request } : {}) })}><span>{n}</span></button>)}
          </nav>
          <section className="card">
            {!list.data ? <div className="empty">Loading…</div> : !list.data.length ? <div className="empty">{view === "review" ? "No task is waiting for approval." : "No tasks here."}</div> : <TaskTable tasks={list.data} showRequest />}
          </section>
        </div>
        <p className="hint">AI coding tools such as Claude Code get ready tasks through the CourageMFG MCP server (mcp.couragemfg.com), work in a branch, record tests and send each task here for approval.</p>
      </div>
    </>
  );
}

export function TaskPage() {
  const { number = "" } = useParams();
  const [tick, setTick] = useState(0);
  const t = useLoad(() => factory.task(number), [number, tick]);
  const me = useLoad(() => api.me());
  const [err, setErr] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setErr(null); setNote(null);
    try { await fn(); setNote(done ?? null); setTick((x) => x + 1); return true; } catch (x) { setErr(x); return false; }
  };
  const d = t.data;
  if (t.error) return <div className="page"><ErrorBox error={t.error} /><Link to="/factory/tasks">Back to tasks</Link></div>;
  if (!d) return <div className="page"><div className="empty">Loading…</div></div>;
  const myId = me.data?.id;
  const mine = d.claimedVia === "app" && d.claimedBy === myId;
  const canApprove = d.state === "review" && !mine && !d.approvals.some((a) => a.by === myId);
  const ask = (q: string) => { const v = prompt(q); return v && v.trim() ? v.trim() : null; };
  return (
    <>
      <Bar status={<>{d.number} · <Chip state={d.state} /></>}>
        {d.state === "ready" && <button className="cmd primary" onClick={() => act(() => factory.taskAct(d.number, "claim"), "The task is yours. Work in a test copy.")}>Claim the task</button>}
        {d.state === "in_progress" && mine && <button className="cmd primary" disabled={d.missing.length > 0} onClick={() => act(() => factory.taskAct(d.number, "submit"), d.approvalsNeeded ? "Sent for approval." : "Done. A report needs no approval.")}>{d.approvalsNeeded ? "Send for approval" : "Mark done"}</button>}
        {d.state === "in_progress" && mine && <button className="cmd" onClick={() => act(() => factory.taskAct(d.number, "release"), "Released. The task is ready again.")}>Release</button>}
        {canApprove && <button className="cmd primary" onClick={() => act(() => factory.taskAct(d.number, "approve"), "Approved.")}>Approve{d.approvalsNeeded > 1 ? ` (${d.approvals.length + 1} of ${d.approvalsNeeded})` : ""}</button>}
        {d.state === "review" && <button className="cmd" onClick={() => { const why = ask("What must change?"); if (why) act(() => factory.taskAct(d.number, "send-back", { reason: why }), "Sent back."); }}>Send back</button>}
        {["draft", "ready", "in_progress", "review"].includes(d.state) && <button className="cmd" onClick={() => { const why = ask("Why cancel this task?"); if (why) act(() => factory.taskAct(d.number, "cancel", { reason: why }), "Cancelled."); }}>Cancel</button>}
      </Bar>
      <div className="page">
        <div className="page-title"><h1>{d.title}</h1><span className="sub">{d.number} · {d.changeTypeLabel} on <Link to={`/factory/r/${d.request.number}`}>{d.request.number}</Link> · {d.request.title}</span></div>
        <ErrorBox error={err} />
        {note && <div className="msg info">{note}</div>}
        {d.state === "review" && mine && <div className="msg info">You did this task, so another person must approve it.</div>}
        {d.state === "in_progress" && d.claimedVia === "mcp" && <div className="msg info">{d.agent ?? "An AI tool"} is doing this task. You approve it when it is sent for approval.</div>}
        <div className="split">
          <div className="grow" style={{ gap: 12 }}>
            <section className="card">
              <div className="mx-sec"><h2>What to do</h2>{d.description ? <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{d.description}</p> : <p className="muted" style={{ margin: 0 }}>See the design.</p>}</div>
              <div className="mx-sec"><dl className="mx-dl"><dt>How to test it</dt><dd>{d.tests || "–"}</dd><dt>How to undo it</dt><dd>{d.rollback || d.undo}</dd></dl></div>
            </section>
            <Evidence d={d} mine={mine} act={act} />
            {d.design && (
              <details className="card mx-sec"><summary><b>Design version {d.design.version}</b> <span className="muted">{d.design.approvedAt ? "approved" : "not approved"}</span></summary>
                <p style={{ whiteSpace: "pre-wrap" }}>{d.design.body}</p>
                {d.design.acceptance.length > 0 && <ul className="if-list">{d.design.acceptance.map((a, i) => <li key={i}>{a}</li>)}</ul>}
              </details>)}
          </div>
          <aside className="side">
            <section className="card mx-sec"><h2>Details</h2>
              <dl className="mx-dl">
                <dt>Status</dt><dd><Chip state={d.state} /></dd>
                <dt>Change type</dt><dd>{d.changeTypeLabel}</dd>
                <dt>Done by</dt><dd>{d.claimedBy ? who(d, true) : d.doneBy === "ai" ? "AI" : "A person"}</dd>
                <dt>Approval</dt><dd>{d.approvalsNeeded ? cap(d.approverRoles.join(" and ")) : "Not needed"}</dd>
                <dt>Request</dt><dd><Link to={`/factory/r/${d.request.number}`}>{d.request.number}</Link></dd>
                <dt>Target</dt><dd>{d.requestDetail.measure ? <>{d.requestDetail.measure}: {val(d.requestDetail.currentValue, d.requestDetail.unit)} → {val(d.requestDetail.targetValue, d.requestDetail.unit)}</> : "–"}</dd>
              </dl>
            </section>
            {d.approvals.length > 0 && <section className="card mx-sec"><h2>Approvals</h2><ul className="mx-hist">{d.approvals.map((a, i) => <li key={i}><span className="when">{when(a.at)}</span><span>{a.byName ?? a.by}<span className="muted"> · {a.role}</span></span></li>)}</ul></section>}
            <section className="card mx-sec"><h2>Log</h2>
              <ul className="mx-hist">{d.log.slice().reverse().map((l, i) => (
                <li key={i}><span className="when">{when(l.at)}</span><span>{logText(l)}<span className="muted"> · {l.via === "mcp" ? `${d.agent ?? "AI tool"} for ${l.actorName ?? "–"}` : l.actorName ?? "–"}</span></span></li>))}
              </ul>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}

function logText(l: { kind: string; body: any }) {
  const b = l.body ?? {};
  switch (l.kind) {
    case "claim": return b.agent ? `Claimed by ${b.agent}` : "Claimed";
    case "note": return b.text;
    case "link": return Object.entries(b).map(([k, v]) => `${k === "testCopy" ? "Test copy" : k === "branch" ? "Branch" : "Link"}: ${v ?? "removed"}`).join(" · ");
    case "test": return `Test ${b.passed ? "passed" : "failed"}: ${b.name}`;
    case "cost": return `AI cost $${Number(b.usd).toFixed(2)}`;
    case "submit": return b.note ? `Sent for approval: ${b.note}` : "Sent for approval";
    case "approve": return `Approved as ${b.role}${b.note ? `: ${b.note}` : ""}`;
    case "send_back": return `Sent back: ${b.reason}`;
    case "cancel": return `Cancelled: ${b.reason}`;
    default: return l.kind;
  }
}

function Evidence({ d, mine, act }: { d: TaskDetail; mine: boolean; act: Act }) {
  const [b, setB] = useState({ branch: d.branch ?? "", testCopy: d.testCopy ?? "", link: d.link ?? "" });
  const [test, setTest] = useState({ name: "", passed: "true", output: "" });
  const [n, setN] = useState("");
  useEffect(() => setB({ branch: d.branch ?? "", testCopy: d.testCopy ?? "", link: d.link ?? "" }), [d.branch, d.testCopy, d.link]);
  const editable = d.state === "in_progress" && mine;
  return (
    <section className="card">
      <div className="mx-sec">
        <h2>Test copy and tests</h2>
        {d.state === "in_progress" && d.missing.length > 0 && <div className="msg warn">Before it can be sent for approval, add {d.missing.join("; ")}.</div>}
        {editable ? (
          <form onSubmit={(e) => { e.preventDefault(); act(() => factory.taskUpdate(d.number, b), "Saved."); }}>
            <div className="form-grid">
              <div className="field"><label htmlFor="ev-tc">Test copy (Neon branch)</label><input id="ev-tc" className="input" value={b.testCopy} onChange={(e) => setB({ ...b, testCopy: e.target.value })} /></div>
              <div className="field"><label htmlFor="ev-br">Code branch</label><input id="ev-br" className="input" value={b.branch} onChange={(e) => setB({ ...b, branch: e.target.value })} /></div>
              <div className="field"><label htmlFor="ev-ln">Link to the change</label><input id="ev-ln" className="input" placeholder="https://…" value={b.link} onChange={(e) => setB({ ...b, link: e.target.value })} /></div>
            </div>
            <button className="btn">Save</button>
          </form>
        ) : (
          <dl className="mx-dl">
            <dt>Test copy</dt><dd>{d.testCopy ?? "–"}</dd>
            <dt>Code branch</dt><dd>{d.branch ?? "–"}</dd>
            <dt>Link</dt><dd>{d.link ? <a href={d.link} target="_blank" rel="noreferrer">{d.link}</a> : "–"}</dd>
          </dl>
        )}
      </div>
      <div className="mx-sec">
        {d.testResults.length ? (
          <ul className="if-tests">{d.testResults.map((r) => (
            <li key={r.name}><span className={`if-chip ${r.passed ? "ok" : "danger"}`}>{r.passed ? "Passed" : "Failed"}</span><b>{r.name}</b>{r.output && <span className="muted">{r.output}</span>}</li>))}
          </ul>) : <p className="muted" style={{ margin: 0 }}>No test results yet.</p>}
        {editable && (
          <form className="if-testform" onSubmit={(e) => { e.preventDefault(); act(() => factory.taskTest(d.number, { name: test.name, passed: test.passed === "true", output: test.output }), "Test recorded.").then((ok) => ok && setTest({ name: "", passed: "true", output: "" })); }}>
            <input className="input" aria-label="Test name" required placeholder="Test, e.g. Replay the last 60 days" value={test.name} onChange={(e) => setTest({ ...test, name: e.target.value })} />
            <select className="select" aria-label="Result" value={test.passed} onChange={(e) => setTest({ ...test, passed: e.target.value })}><option value="true">Passed</option><option value="false">Failed</option></select>
            <input className="input" aria-label="Output" placeholder="Short output" value={test.output} onChange={(e) => setTest({ ...test, output: e.target.value })} />
            <button className="btn">Record test</button>
          </form>)}
      </div>
      {!["deployed", "cancelled"].includes(d.state) && (
        <form className="mx-sec if-testform" onSubmit={(e) => { e.preventDefault(); act(() => factory.taskUpdate(d.number, { note: n }), "Note added.").then((ok) => ok && setN("")); }}>
          <input className="input" aria-label="Note" placeholder="Add a note" value={n} onChange={(e) => setN(e.target.value)} />
          <button className="btn" disabled={!n.trim()}>Add note</button>
        </form>)}
    </section>
  );
}
